import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, ArrowLeft, Crown, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const FREE = ["Fetch news, all 9 categories", "Auto-refresh 15 / 30 min", "3 AI rewrites per day", "English rewrites only", "Basic SEO metadata"];
const PAID = ["Everything in Free", "Unlimited AI rewrites", "All 5 languages (EN/HI/BN/TA/MR)", "Minimalist AI image generation", "Full technical SEO suite", "Copyright infringement checks", "Priority processing"];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Subscribe() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [config, setConfig] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const premium = user?.plan === "premium" || user?.is_lifetime_free;

  const pay = async () => {
    if (!config?.configured) {
      toast.info("Payment setup pending", { description: "Razorpay keys not configured yet. Add them to enable checkout." });
      return;
    }
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load Razorpay");
      const { data: order } = await api.post("/payments/create-order");
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "TD News Fetch",
        description: "Premium subscription ($5)",
        order_id: order.order_id,
        prefill: { name: user?.name, email: user?.email },
        theme: { color: "#111110" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            await refresh();
            toast.success("Welcome to Premium!");
            navigate("/app");
          } catch (e) {
            toast.error("Verification failed", { description: apiErr(e) });
          }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error("Payment error", { description: apiErr(e) });
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain-bg">
      <header className="h-14 border-b border-border bg-white flex items-center px-5">
        <Button data-testid="back-btn" variant="ghost" className="rounded-none font-mono text-xs" onClick={() => navigate("/app")}>
          <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" /> Back to app
        </Button>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-14">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#111110] mb-3">Pricing</p>
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tighter leading-none mb-3">
          Unlock the full newsroom.
        </h1>
        <p className="text-muted-foreground max-w-lg mb-12">
          Free forever for casual use. Go Premium for unlimited multilingual rewrites, AI images and a full SEO suite — pay by UPI or credit card.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
          {/* Free */}
          <div className="border border-border bg-white p-8" data-testid="plan-free">
            <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Free</p>
            <p className="font-heading font-extrabold text-4xl mb-6">$0</p>
            <ul className="space-y-3 mb-8">
              {FREE.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-[#111110] mt-0.5 shrink-0" aria-hidden="true" /> {f}
                </li>
              ))}
              <li className="flex items-start gap-2 text-sm text-muted-foreground">
                <X className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" /> No AI image / multilingual
              </li>
            </ul>
            <Button disabled variant="outline" className="w-full rounded-none font-mono text-xs uppercase">Current tier</Button>
          </div>

          {/* Premium */}
          <div className="border-2 border-[#ffce00] bg-white p-8 relative" data-testid="plan-premium">
            <span className="absolute top-0 right-0 bg-[#ffce00] text-black font-mono text-[10px] uppercase tracking-wider px-3 py-1">Recommended</span>
            <p className="font-mono text-xs uppercase tracking-wider text-[#111110] mb-2 flex items-center gap-1">
              <Crown className="w-3.5 h-3.5" aria-hidden="true" /> Premium
            </p>
            <p className="font-heading font-extrabold text-4xl mb-1">$5<span className="text-base font-normal text-muted-foreground">/mo</span></p>
            <p className="font-mono text-[11px] text-muted-foreground mb-6">UPI · Credit / Debit card via Razorpay</p>
            <ul className="space-y-3 mb-8">
              {PAID.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-[#111110] mt-0.5 shrink-0" aria-hidden="true" /> {f}
                </li>
              ))}
            </ul>
            {premium ? (
              <Button disabled className="w-full rounded-none bg-[#111110] text-[#ffce00] font-mono text-xs uppercase" data-testid="already-premium">
                <Check className="w-4 h-4 mr-2" aria-hidden="true" /> You are Premium
              </Button>
            ) : (
              <Button data-testid="pay-btn" onClick={pay} disabled={paying}
                className="w-full rounded-none bg-[#111110] hover:bg-[#ffce00] text-white hover:text-black transition-colors py-6 font-mono text-xs uppercase tracking-wider">
                {paying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" /> : null}
                {config && !config.configured ? "Payments on hold" : "Upgrade — pay $5"}
              </Button>
            )}
            {config && !config.configured && (
              <p className="font-mono text-[10px] text-muted-foreground mt-3">
                Paid checkout is temporarily on hold and will be switched on soon.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
